import { html } from '@acme/b2b/utils';
import { CartService, CartServiceModule } from '@b2b/common/services';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { getFullName } from '@b2b/common/services/helpers';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { TODO } from '@b2b/types/core';
import { OrderContact } from '@b2b/types/order';
import { Organization } from '@b2b/types/organization';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { ITimeoutService, growl } from 'angular';
import { CartDirectiveModule } from '../cart/cart.directive';

HeaderDirective.$inject = [
  '$state',
  '$timeout',
  'growl',
  'AuthService',
  'AuthenticationService',
  'ConfigService',
  'CartService',
  'FeatureToggleService',
  'HelpersService',
  'ThemesHelperService',
];

function HeaderDirective(
  $state: StateService,
  $timeout: ITimeoutService,
  growl: growl.IGrowlService,
  AuthService: TODO,
  authenticationService: AuthenticationService,
  ConfigService: TODO,
  CartService: CartService,
  FeatureToggleService: TODO,
  HelpersService: TODO,
  ThemesHelperService: ThemesHelperService,
) {
  HeaderController.$inject = ['$scope'];

  function HeaderController($scope) {
    let user = authenticationService.getUserFromCookie();
    let csr = authenticationService.getCSRUserFromCookie();

    const ctrl = {
      config: {},

      print: false,
      minimal: false,

      isCheckTimerRunning: false,
      cartHint: 'View Cart',
      viewCartHint: 'View Cart',
      emptyCartHint: 'Cart is empty',

      user: user,
      csr: csr,
      hasUserProfileName: false,
      isLoggedIn: user && (user.hasOwnProperty('email') || user.hasOwnProperty('userName')),
      isCartOpen: false,
      cartItemCount: 0,
      cartInitted: false,
      cartService: CartService,
      hideSpinner: false,
      spinnerTimer: null as angular.IPromise<void> | null,
      cartInitTimer: null as angular.IPromise<void> | null,
      useCart: false,
      orderContact: {} as Partial<OrderContact> | null,
      organization: {} as Partial<Organization> | null,
      growlCartMessage: {} as growl.IGrowlMessage,

      hideItems: false,

      init: function () {
        ctrl.config = ConfigService.get('directives', 'header');
        ctrl.print = $scope.print;
        ctrl.minimal = $scope.minimal;
        ctrl.useCart = FeatureToggleService.check('TENANT_CART');

        $scope.$on('header::message', ctrl.onShowMessage);
        $scope.$on('AppController::logout::success', ctrl.onLogoutSuccess);
        $scope.$on('AppController::login::success', ctrl.onLoginSuccess);
        $scope.$on('AppController::cart::open', ctrl.onCartOpen);
        $scope.$on('AppController::cart::close', ctrl.onCartClose);
        $scope.$on('AppController::cart::toggle', ctrl.onCartToggle);
        $scope.$on('$destroy', $scope.$watch('header.cartService.itemCount', ctrl.onCartServiceItemCountUpdate));
        $scope.$on('$destroy', $scope.$watch('header.cartService.events.events.length', ctrl.onCartEventsChanged));
        $scope.$on('$destroy', $scope.$watch('header.cartService.initted', ctrl.onCartServiceInitted));
        $scope.$on('$destroy', $scope.$watch('header.hideItems', ctrl.onHideItemsUpdate));
        $scope.$on(
          '$destroy',
          $scope.$watchCollection('header.cartService.orderContact', ctrl.onCartServiceOrderContactUpdated),
        );
        $scope.$on(
          '$destroy',
          $scope.$watchCollection('header.cartService.organization', ctrl.onCartServiceOrganizationUpdate),
        );
        $scope.$on('$destroy', ctrl.killTimers);
      },

      killTimers: function () {
        if (ctrl.spinnerTimer) {
          $timeout.cancel(ctrl.spinnerTimer);
        }
        if (ctrl.cartInitTimer) {
          $timeout.cancel(ctrl.cartInitTimer);
        }
      },

      updateCartHint: function (hint) {
        ctrl.cartHint = ctrl.cartService.itemCount || ctrl.cartService.events.events.length ? hint : ctrl.emptyCartHint;
      },

      getTemplate: function () {
        if (ctrl.print) {
          return 'common/directives/header/print.tpl.html';
        }

        if (ctrl.minimal) {
          return 'common/directives/header/minimal.tpl.html';
        }

        return 'common/directives/header/application.tpl.html';
      },

      launchCart: function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (ctrl.cartService.itemCount || ctrl.cartService.events.events.length) {
          const organization = ctrl.cartService.getOrganization();

          if (organization && organization.id) {
            ctrl.isCartOpen = true;
          } else {
            $state.go('application.cart');
          }
        }
      },

      refresh: function () {
        window.location.reload();
        $state.go($state.current, $state.params, {
          notify: true,
          reload: true,
          inherit: false,
        });
      },

      printPage: function () {
        $scope.$emit('Header::print');
      },

      getUserFullName: function () {
        return authenticationService.getUserFullName();
      },
      getFullName: function (contact) {
        return getFullName(contact);
      },

      getConfig: function (name) {
        let result = false;

        if (ctrl.config.hasOwnProperty(name)) {
          result = ctrl.config[name];
        }

        return result;
      },

      getHomeLink: function () {
        return AuthService.getHomeUrl();
      },

      getCompanyName: function () {
        return ThemesHelperService.getCompany('name') || 'Tenant ID: ' + ctrl.user?.tenantId;
      },

      logout: function () {
        user = null;
        csr = null;
        ctrl.user = user;
        ctrl.csr = csr;
        ctrl.isLoggedIn = false;
      },

      onLogoutSuccess: function (e, data) {
        ctrl.logout();
      },

      onLoginSuccess: function (e, data) {
        console.log('header::onLoginSuccess');
      },

      /**
       * Opens cart and invokes growl notification
       * data parameter requires itemName and assumes
       * a cart item otherwise defaults to 'Item'
       */
      onCartOpen: function (e, data) {
        const name = (data && data.itemName) || 'Item';

        ctrl.showMessage({ message: name + ' added to the cart.', fallback: true });

        ctrl.isCartOpen = true;
      },

      /**
       * Uses JQuery event mechanism instead of angular
       */
      showMessage: function (props: { message?: string; fallback: boolean }) {
        const markup = html`
          <a
            class="header-cart-growl"
            onclick="event.preventDefault();event.stopPropagation();$(document).trigger('Header::Top')"
            class="btn btn-link">
            <span class="fa fa-2x fa-shopping-cart"></span>
            <span>${props.message || ''} </span>
          </a>
        `;

        $(document).off('Header::Top');
        $(document).on('Header::Top', () => {
          ctrl.growlCartMessage.destroy();

          if (props.fallback) {
            // Fallback in case user has clicked away
            ctrl.isCartOpen = true;
          }

          $('html, body').animate({ scrollTop: 0 }, 'fast');
        });

        ctrl.growlCartMessage = growl.success(markup);
      },

      onCartClose: function (e, data) {
        ctrl.isCartOpen = false;
      },

      onCartToggle: function (e, data) {
        ctrl.isCartOpen = data.open;
      },

      onCartServiceItemCountUpdate: function (newValue, oldValue) {
        if (newValue) {
          ctrl.cartHint = ctrl.viewCartHint;
        } else {
          ctrl.cartHint = ctrl.emptyCartHint;
        }
      },

      onCartServiceInitted: function (newValue, oldValue) {
        if (newValue) {
          ctrl.killTimers();
          ctrl.cartInitTimer = $timeout(() => {
            ctrl.cartInitted = newValue;
            ctrl.spinnerTimer = $timeout(() => {
              ctrl.hideSpinner = true;
            }, 500);
          }, 1000);
        }
      },

      onCartEventsChanged: function (newValue, oldValue) {
        if (newValue) {
          ctrl.cartHint = ctrl.viewCartHint;
        } else {
          ctrl.cartHint = ctrl.emptyCartHint;
        }
      },

      onCartServiceOrderContactUpdated: function (newValue, oldValue) {
        ctrl.orderContact = ctrl.cartService.getOrderContact();
      },

      onCartServiceOrganizationUpdate: function (newValue, oldValue) {
        if (newValue && newValue.id) {
          ctrl.organization = ctrl.cartService.getOrganization();
        } else {
          ctrl.organization = {};
        }
      },

      onShowMessage: function (e, data) {
        ctrl.showMessage(data);
      },

      onHideItemsUpdate: function (newValue, oldValue) {
        $scope.$emit('header::hideItems', newValue);
      },
    };

    $scope.header = ctrl;
    ctrl.init();
  }

  return {
    restrict: 'AE',
    replace: true,
    templateUrl: 'common/directives/header/header.tpl.html',
    scope: {
      print: '@',
      minimal: '@',
    },

    link: function ($scope, $element, $attrs) {
      // Modified after controller runs
    },

    controller: HeaderController,
  };
}

export const HeaderDirectiveModule = angular
  .module('acme.common.directive.header', [
    uiRouter,
    'ui.bootstrap',
    'angular-growl',
    'acme.common.service.api',
    'acme.common.service.auth',
    AuthenticationServiceModule,
    'acme.common.service.permissions',
    'acme.common.service.validate',
    'acme.common.service.config',
    CartServiceModule,
    'acme.common.service.featureToggle',
    'acme.common.service.helpers',
    CartDirectiveModule,
    'acme.common.service.themesHelper',
  ])
  .directive('header', HeaderDirective).name;
